import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import { SocietyEquityMarketStatus } from 'types/Society';
import IncentivesMarketStatusBadge, {
  incentivesMarketStatusConfig,
} from './IncentivesMarketStatusBadge';
import IncentivesMarketAdministratorBadge from './IncentivesMarketAdministratorBadge';

type IncentivesMarketAdminPanelHeaderProps = {
  societyId: string;
  societyName: string;
  incentivesMarketStatus?: SocietyEquityMarketStatus;
  onIncentivesMarketStatusChange?: (status: SocietyEquityMarketStatus) => void; // eslint-disable-line no-unused-vars
};

const IncentivesMarketAdminPanelHeader: FC<IncentivesMarketAdminPanelHeaderProps> = ({
  societyId,
  societyName,
  incentivesMarketStatus = SocietyEquityMarketStatus.OPEN,
  onIncentivesMarketStatusChange,
}) => {
  const { t } = useTranslate();
  const location = useLocation();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const pq = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      {
        label: t('IncentivesMarket'),
        to: `/mercado-de-incentivos/${societyId}${pq}`,
      },
      { label: t('AdminPanel'), isActive: true },
    ],
    [t, societyName, societyId, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <div className="d-flex align-items-center">
              <h3 className="nk-block-title page-title">{t('AdminPanel')}</h3>

              <IncentivesMarketAdministratorBadge />
              <IncentivesMarketStatusBadge status={incentivesMarketStatus} />
            </div>

            <div className="nk-block-des text-soft">
              <p>{t('IncentivesMarketAdminPanelDescription')}</p>
            </div>
          </div>

          {onIncentivesMarketStatusChange && (
            <div className="nk-block-head-content d-flex align-items-center gap-2">
              {/* @ts-ignore */}
              <OperationMenuDots
                title={t('MarketStatus')}
                variant="primary"
                menuOptions={Object.entries(incentivesMarketStatusConfig).map(
                  ([status, config]) => ({
                    icon: config.icon,
                    text: t(config.labelKey),
                    action: () =>
                      onIncentivesMarketStatusChange(
                        status as SocietyEquityMarketStatus
                      ),
                    dropdown: true,
                    disabled:
                      incentivesMarketStatus ===
                      (status as SocietyEquityMarketStatus),
                  })
                )}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IncentivesMarketAdminPanelHeader;
